const destinations = [
  {
    name: "Canada",
    context: "Livestock purchasing in Saskatchewan and Manitoba, agricultural trade shows, business travel via Toronto and Calgary",
    urgency: "Spring and fall cattle buying seasons create last-minute passport surges",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico",
    context: "Agricultural export meetings in Monterrey, manufacturing facility visits, family vacation to Cancun and Los Cabos",
    urgency: "Business travel often booked 1–2 weeks out when export deals close",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Europe",
    context: "MWSU study-abroad programs in Spain, Germany, and France; healthcare conference travel; family heritage tourism",
    urgency: "Study-abroad deadlines hit October and February after program acceptance",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Caribbean & Bahamas",
    context: "Cruise departures from Galveston and New Orleans, family beach vacations, honeymoon travel from St. Joseph families",
    urgency: "Last-minute cruise deals create 2–4 week passport windows across Northwest Missouri",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Australia & New Zealand",
    context: "Livestock genetics and breeding stock travel — a significant niche for Missouri beef cattle industry professionals",
    urgency: "Long-haul livestock purchasing trips often planned quickly around market conditions",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom",
    context: "Healthcare conference travel from Mosaic Life Care, executive business meetings, family tourism along the I-29 corridor",
    urgency: "Conference registration is months out but passport check often missed until flight booking",
    icon: "ri-map-pin-2-line",
  },
];

const commonSituations = [
  {
    stat: "1 Hour",
    label: "to MCI — but the nearest passport agency (Chicago) is 500 miles away",
  },
  {
    stat: "60%",
    label: "of St. Joseph expedited requests involve Canada or Mexico agriculture-related business travel",
  },
  {
    stat: "Oct & Feb",
    label: "peak MWSU study-abroad deadline months — when many first-time applicants discover they need expedited help",
  },
  {
    stat: "2–3 Weeks",
    label: "typical notice Northwest Missouri agriculture professionals have before an international trade trip",
  },
];

export default function StJosephDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where St. Joseph &amp; Northwest Missouri Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            These are the destinations and travel patterns we see most from St. Joseph-area clients. Understanding local travel behavior is part of why our document review catches issues before they derail your business trip, study-abroad program, or family vacation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Missouri Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across Missouri with the same expedited passport processing and document review expertise. Looking for service in another community?
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/kansas-city" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Kansas City <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/st-louis" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                St. Louis <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/springfield-mo" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Springfield <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}